/**
 * Modo discreto em JSON cru (r9 visual M1, r9 spec M3): auditoria, payload de webhook, argumentos e resultado do MCP.
 *
 * A censura é por chave, recursiva, e devolve uma cópia (o original não muda). Review r14: o tipo de cada chave vem da regra
 * única `sensivelPorNome` (`nucleo/sensivel-por-nome.ts`), com o termo em qualquer posição da chave; a lista abaixo é o que
 * cada tipo vira aqui:
 *  - dinheiro: chave terminada em `centavos` (`valor_centavos` e, r10 spec B3, o camelCase `valorCentavos`,
 *    `custoUnitarioCentavos`) e valor, total, saldo, preço, custo, salário, juros, multa, desconto (também `valorTotal`);
 *  - documento, CPF, CNPJ, RG, CNH, PIS, `numero_documento`: mascarado como na tela (`•••.456.789-••`) ou oculto; o
 *    `numero` de documento pessoal também (entidade `pessoas.documento` ou objeto com `tipo` de documento, r10 spec B3);
 *  - e-mail e telefone: mascarados como nos formatadores (`g•••@d•••.com`, `(••) •••••-4321`);
 *  - nascimento, endereço (logradouro, número, complemento, bairro, CEP, endereço em texto), Pix, agência e conta: ocultos.
 *    O `numero` da casa some dentro de um objeto `endereco`, no registro da entidade de endereço (`cadastros.endereco`) e em
 *    qualquer objeto que tenha campos de endereço no mesmo nível (`logradouro`, `cep`, `bairro`, `complemento`; r10 visual B2);
 *  - nome de pessoa ou empresa, só com a opção de nomes (r10 visual B3): `titular`, `razao_social`, `nome_fantasia`,
 *    `nome_completo` e `<parceiro|cliente|...>_nome` sempre; o `nome` solto quando o registro é de parceiro, contato,
 *    funcionário ou conta bancária (pela entidade da auditoria ou do chatter) ou quando a entidade não é conhecida (payload de
 *    webhook, MCP). O `nome` de produto, papel ou usuário do sistema continua;
 *  - campo personalizado (dentro de `extras`, ou o campo `extras.<chave>` do diff; review r15 spec M1): a regra EFETIVA
 *    `sensivelDoExtra`, a mesma da ficha, da Tabela e do Editar. Número e data somem sempre; texto e lista pela chave, pelo
 *    rótulo ou pela marca `sensivel` da definição (`defsExtras`, que a tela passa); sem a definição, o tipo vem do valor
 *    gravado (número, data ISO); sim ou não fica;
 *  - número inteiro com o formato de telefone brasileiro contíguo (10 a 13 dígitos, com ou sem o 55, DDD válido) sai como
 *    telefone, fora das chaves de id e de instante (review r22 segurança, sondagem: `telefoneNumerico`);
 *  - qualquer outro texto passa por `censurarTexto` (valor, documento, e-mail, telefone e CEP dentro de frase) e, com a opção
 *    de nomes, por `textoSemNomes` com os nomes conhecidos do registro (r11 visual B1: o título da oportunidade e a observação
 *    que citam o cliente): os de pessoa do próprio registro e os que a tela passar (o parceiro do `parceiro_id`).
 * Fora do modo discreto, volta o próprio valor.
 */
import { discretoAtivo, iniciais, mascararDocumento, mascararEmail, mascararTelefone, nomesOcultos, OCULTO } from './discreto';
import { censurarTexto, telefoneNumerico, textoLivre, textoSemNomes } from './formato';
import { ehDocumentoPorNome, sensivelDoExtra, sensivelPorNome, type DefExtraSensivel, type TipoSensivel } from './sensivel-por-nome';

/** Definições dos campos personalizados da entidade, por chave (sem o `extras.`): tipo, rótulo e a marca `sensivel`. */
export type DefsExtras = ReadonlyMap<string, DefExtraSensivel>;

/**
 * Review r21 spec (M2): payload de um conector de WhatsApp (Integrações > Webhooks > Entrada: o webhook da Meta e os lotes
 * dos gateways não oficiais). As chaves do protocolo que são telefone ou nome de pessoa seguem `sensivelPorNome` com a opção
 * `whatsapp`; o `nome` solto é de pessoa (o contato).
 */
export const ENTIDADE_WEBHOOK_WHATSAPP = 'integracoes.webhook.whatsapp';

/** Entidade cujo `numero` é o número de um documento pessoal. */
const ENTIDADES_DOCUMENTO = new Set(['pessoas.documento']);
/** Entidade cujo registro é um endereço: o `numero` é o da casa (r10 visual B2). */
const ENTIDADES_ENDERECO = new Set(['cadastros.endereco', 'pessoas.endereco', 'configuracoes.endereco']);
/** Entidade cujo `nome` é de pessoa ou empresa dos dados do negócio (r10 visual B3). */
const ENTIDADES_PESSOA = new Set(['cadastros.parceiro', 'cadastros.contato', 'cadastros.conta_bancaria', 'conversas.contato', 'pessoas.funcionario', 'pessoas.dependente', ENTIDADE_WEBHOOK_WHATSAPP]);
/** `tipo` de um objeto de documento pessoal: o `numero` dele é o documento. */
const TIPO_DOCUMENTO = /^(rg|cpf|cnh|cnpj|pis|ctps|passaporte|titulo|titulo_eleitor|reservista|rne|crnm)$/i;
/** Lista dentro do registro cujos itens são pessoas (`contatos: [{ nome }]`). */
const LISTA_PESSOAS = /^(contatos|dependentes|socios|titulares)$/i;
/** Objeto de endereço: dentro dele, `numero` também é oculto. */
const OBJETO_ENDERECO = /(^|_)(endereco|enderecos|address)$/i;
/** Campos de endereço que, no mesmo nível, fazem do `numero` o número da casa. */
const IRMAOS_ENDERECO = ['logradouro', 'cep', 'bairro', 'complemento'];

interface Contexto {
  /** Dentro de um objeto `endereco`: todo texto (menos cidade, UF) some. */
  emEndereco: boolean;
  /** O `numero` deste nível é o de um documento pessoal. */
  emDocumento: boolean;
  /** O `numero` deste nível é o da casa (entidade de endereço ou campos de endereço ao lado). */
  numeroDaCasa: boolean;
  /** O `nome` deste nível é de pessoa ou empresa. */
  nomeDePessoa: boolean;
  /** Nomes conhecidos do registro, trocados pelas iniciais dentro do texto livre (com a opção de nomes). */
  nomes: string[];
  /** As folhas deste nível são campos personalizados (filhos diretos de `extras`). */
  emExtras: boolean;
  /** Definições dos campos personalizados da entidade (a tela passa; sem elas, a regra usa o valor). */
  defsExtras?: DefsExtras;
  /** Review r21 spec (M2): payload de um conector de WhatsApp (chaves do protocolo: telefone e nome). */
  whatsapp?: boolean;
}

const temIrmaosDeEndereco = (o: unknown) => !!o && typeof o === 'object' && !Array.isArray(o) && IRMAOS_ENDERECO.some((k) => Object.hasOwn(o, k));

/**
 * Uma folha do JSON. Review r14 (spec M2): o tipo vem da regra ÚNICA por nome (`sensivelPorNome`, termo em qualquer posição:
 * `extras.salario`, `cep_cobranca`, `nascimento_conjuge`), a mesma da ficha, da Tabela e do Editar. Dinheiro some quando há
 * número; documento mostra o miolo quando tem 11 ou 14 dígitos; e-mail e telefone como os formatadores; endereço, nascimento,
 * Pix, agência e conta somem; nome de pessoa vira iniciais com a opção de nomes.
 */
function folha(chave: string, v: string | number | boolean, c: Contexto): unknown {
  if (typeof v === 'boolean') return v;
  let tipo: TipoSensivel | null;
  if (c.emExtras) {
    // Review r15 spec M1: campo personalizado pela regra efetiva (número e data sempre; texto e lista pela chave, pelo rótulo
    // ou pela marca da definição). Dinheiro pelo nome some inteiro, como na ficha.
    const regra = sensivelDoExtra(chave, c.defsExtras?.get(chave), v);
    if (regra === 'oculto' || regra === 'dinheiro') return OCULTO;
    tipo = regra;
  } else tipo = sensivelPorNome(chave, { emEndereco: c.emEndereco || c.numeroDaCasa, whatsapp: c.whatsapp });
  if (tipo === 'dinheiro' && (typeof v === 'number' || /\d/.test(v))) return OCULTO;
  const s = String(v);
  if (ehDocumentoPorNome(tipo) || (c.emDocumento && /^numero$/i.test(chave))) return /^\D*(\d\D*){11}$|^\D*(\d\D*){14}$/.test(s) ? mascararDocumento(s) : OCULTO;
  if (tipo === 'email') return mascararEmail(s);
  if (tipo === 'telefone') return /\d{4}/.test(s.replace(/\D/g, '')) ? mascararTelefone(s) : OCULTO;
  if (tipo && tipo !== 'dinheiro' && tipo !== 'nome') return OCULTO;
  // Review r22 segurança (sondagem): telefone como número (e não texto) numa chave fora da lista sai mascarado, como o texto.
  if (typeof v === 'number') return telefoneNumerico(chave, v) ? mascararTelefone(s) : v;
  if (nomesOcultos() && (tipo === 'nome' || (c.nomeDePessoa && /^nome$/i.test(chave)))) return iniciais(s);
  return censurarTexto(c.nomes.length ? textoSemNomes(s, ...c.nomes) : s);
}

function censurar(v: unknown, chave: string, c: Contexto): unknown {
  if (v === null || v === undefined) return v;
  if (Array.isArray(v)) return v.map((x) => censurar(x, chave, c));
  if (typeof v === 'object') {
    const tipo = (v as Record<string, unknown>).tipo;
    const dentro: Contexto = {
      emEndereco: c.emEndereco || OBJETO_ENDERECO.test(chave),
      emDocumento: c.emDocumento || (typeof tipo === 'string' && TIPO_DOCUMENTO.test(tipo)),
      // O nível de cima não passa para o de baixo (o `numero` de um item de fatura dentro de um parceiro não é da casa); só a
      // raiz herda o da entidade (`cadastros.endereco`).
      numeroDaCasa: temIrmaosDeEndereco(v) || (chave === '' && c.numeroDaCasa),
      nomeDePessoa: c.nomeDePessoa || LISTA_PESSOAS.test(chave),
      nomes: c.nomes,
      emExtras: chave === 'extras',
      defsExtras: c.defsExtras,
      whatsapp: c.whatsapp,
    };
    return Object.fromEntries(Object.entries(v as Record<string, unknown>).map(([k, x]) => [k, censurar(x, k, dentro)]));
  }
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
    // Endereço em texto dentro de um objeto `endereco` com outro nome de chave (rua, linha1): oculto também.
    if (c.emEndereco && typeof v === 'string' && !/^(cidade|uf|estado|pais|tipo|id)$/i.test(chave)) return OCULTO;
    return folha(chave, v, c);
  }
  return v;
}

/** Contexto da raiz pela entidade (auditoria, chatter); sem entidade (webhook, MCP), o `nome` solto conta como de pessoa. */
function contextoDaEntidade(entidade: string | null | undefined, registro?: unknown, nomes: string[] = [], defsExtras?: DefsExtras): Contexto {
  return {
    emEndereco: false,
    emDocumento: !!entidade && ENTIDADES_DOCUMENTO.has(entidade),
    numeroDaCasa: (!!entidade && ENTIDADES_ENDERECO.has(entidade)) || temIrmaosDeEndereco(registro),
    nomeDePessoa: !entidade || ENTIDADES_PESSOA.has(entidade),
    nomes,
    emExtras: false,
    defsExtras,
    whatsapp: entidade === ENTIDADE_WEBHOOK_WHATSAPP,
  };
}

/**
 * Nomes de pessoa ou empresa que o registro traz (`razao_social`, `parceiro_nome`, o `nome` de parceiro, contato ou
 * funcionário...), para sumir também de dentro do texto livre do mesmo registro. Vazio sem a opção de nomes.
 */
export function nomesDoRegistro(valor: unknown, entidade?: string | null): string[] {
  if (!nomesOcultos()) return [];
  const achados = new Set<string>();
  const whatsapp = entidade === ENTIDADE_WEBHOOK_WHATSAPP;
  const andar = (v: unknown, chave: string, dePessoa: boolean, nivel: number) => {
    // Review r22 robustez (O2): o `profile.name` do webhook da Meta fica no 9º nível (entry, changes, value, contacts, profile).
    if (v === null || v === undefined || nivel > 16) return;
    if (Array.isArray(v)) return v.forEach((x) => andar(x, chave, dePessoa, nivel + 1));
    if (typeof v === 'object') {
      const pessoa = dePessoa || LISTA_PESSOAS.test(chave);
      for (const [k, x] of Object.entries(v as Record<string, unknown>)) andar(x, k, pessoa, nivel + 1);
      return;
    }
    if (typeof v === 'string' && (sensivelPorNome(chave, { whatsapp }) === 'nome' || (dePessoa && /^nome$/i.test(chave)))) achados.add(v);
  };
  andar(valor, '', !entidade || ENTIDADES_PESSOA.has(entidade), 0);
  return [...achados];
}

/**
 * JSON com os dados sensíveis censurados quando o modo discreto está ligado; desligado, o próprio valor. `entidade`
 * (auditoria) diz de onde vem o registro: em `pessoas.documento`, o `numero` é o documento; em `cadastros.endereco`, é o
 * número da casa; em parceiro, contato e funcionário, o `nome` é de pessoa. `defsExtras`: as definições dos campos
 * personalizados da entidade (review r15 spec M1).
 */
export function censurarJson<T>(valor: T, entidade?: string | null, nomes: (string | null | undefined)[] = [], defsExtras?: DefsExtras): T | unknown {
  if (!discretoAtivo()) return valor;
  return censurar(valor, '', contextoDaEntidade(entidade, undefined, juntarNomes(nomesDoRegistro(valor, entidade), nomes), defsExtras));
}

/**
 * Review r22 robustez (O2): texto livre gravado junto de um payload (o erro de um evento de Webhooks > Entrada). No modo
 * discreto, telefone, e-mail e documento somem do texto, e os nomes que o próprio payload traz (com a opção de nomes) viram
 * iniciais. Nome que não está no payload fica (não há como saber que é nome).
 */
export const textoLivreDoPayload = (texto: string | null | undefined, payload: unknown, entidade?: string | null) => textoLivre(texto, ...nomesDoRegistro(payload, entidade));

const juntarNomes = (a: string[], b: (string | null | undefined)[]) => [...new Set([...a, ...b.filter((x): x is string => !!x)])];

/** `JSON.stringify` indentado, já censurado no modo discreto. `nomes`: nomes conhecidos de fora do registro (o parceiro). */
export const jsonCensurado = (valor: unknown, entidade?: string | null, nomes: (string | null | undefined)[] = [], defsExtras?: DefsExtras) =>
  JSON.stringify(censurarJson(valor, entidade, nomes, defsExtras), null, 2) ?? '';

/**
 * Um campo do diff da auditoria ou do histórico do chatter (`campo` pode vir com caminho, `endereco.numero`), com a MESMA
 * censura do JSON: a tabela de mudanças e o "Ver JSON" não discordam (r10 spec B3). `registro` (o antes ou o depois inteiro,
 * quando a tela tem) diz se há campos de endereço ao lado do `numero`. Desligado, o próprio valor.
 */
export function censurarCampo(campo: string, valor: unknown, entidade?: string | null, registro?: unknown, nomes: (string | null | undefined)[] = [], defsExtras?: DefsExtras): unknown {
  if (!discretoAtivo()) return valor;
  const partes = campo.replace(/^extras\./, '').split('.');
  const chave = partes[partes.length - 1] ?? campo;
  const caminho = partes.slice(0, -1);
  const c = contextoDaEntidade(entidade, caminho.length ? undefined : registro, juntarNomes(nomesDoRegistro(registro, entidade), nomes), defsExtras);
  // Review r15 spec M1: `extras.<chave>` é campo personalizado (regra efetiva com a definição).
  if (/^extras\.[^.]+$/.test(campo)) c.emExtras = true;
  if (caminho.some((p) => OBJETO_ENDERECO.test(p))) c.emEndereco = true;
  if (caminho.some((p) => LISTA_PESSOAS.test(p))) c.nomeDePessoa = true;
  return censurar(valor, chave, c);
}
